// eslint-disable-next-line no-unused-vars
import React from 'react';
import { array, bool, object, func, string } from 'prop-types';
import { RadioButtonGroup, RadioButton, Loading, Button, TextArea } from '@carbon/react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import noop from 'lodash/noop';

const StyledQuickAddContainer = styled.div`
  border: 1px solid ${ props => props.theme.products.colors.border };
  border-top: 0;
  padding: 30px 15px;

  .heading {
    margin-bottom: 10px;
    font-size: 0.875rem;
    line-height: 1.125rem;
  }

  .cds--row {
    align-items: flex-start;
    justify-content: space-between;
    margin: 0;
  }

  .cds--btn {
    height: 40px;
    padding: 0 15px;
  }

  .left {
    flex-grow: 1;
    padding-right: 25px;
    max-width: calc(100% - 160px);
  }

  .right {
    min-width: 160px;
  }

  .textarea {
    &.disabled {
      border-radius: 6px;
    }

    padding: 12px;
    background: transparent;
    border: 1px solid ${ props => props.theme.products.colors.border };
    font-size: 0.875rem;
    line-height: 1.375rem;
    color: ${ props => props.theme.colors.darkGray };
    width: auto;

    .cds--text-area__wrapper--cols {
      width: 100%;
      max-width: unset;

      .cds--text-area {
        width: 100%;
        background-color: transparent;
        border: 0;
      }
    }

    p {
      font-size: 0.875rem;
      line-height: 1.375rem;

      .code {
        margin-right: 3px;
      }
    }
  }

  .suggestions {
    border: 1px solid ${ props => props.theme.products.colors.border };
    padding: 21px 12px;
    font-size: 0.875rem;
    line-height: 1.125rem;

    .products {
      padding: 15px 5px;
      margin-top: 23px;
      background-color: #FBFBFB;

      .radioOption {
        &:hover, &:active, &:focus, &.active {
          color: ${ props => props.theme.colors.blue };
        }

        label {
          display: flex;
          align-items: center;
        }

        span {
          text-overflow: ellipsis;

          /* Needed to make it work */
          overflow: hidden;
          white-space: nowrap;
        }

        p {
          display: grid;
          grid-template-columns: auto 1fr;
          grid-column-gap: 30px;
        }
      }
    }

    p {
      font-size: 0.875rem;
      line-height: 1.125rem;

      strong {
        font-weight: 600;
        margin: 0 3px;
      }

      .red {
        margin: 0 3px;
        color: ${ props => props.theme.colors.default };
        white-space: nowrap;
      }
    }

    .error {
      display: flex;
      align-items: center;
      min-height: 38px;
      padding: 5px 10px;
      font-size: 0.875rem;
      line-height: 1.125rem;
      color: ${ props => props.theme.colors.darkGray };
      border: 1px solid ${ props => props.theme.colors.default };

      .red {
        white-space: nowrap;
        color: ${ props => props.theme.colors.default };
      }

      strong {
        font-weight: 600;
      }
    }
  }

  .actions {
    display: flex;
    margin-top: 20px;

    button:not(:last-of-type) {
      margin-right: 15px;
    }
  }

  .addToCart {
    max-width: 218px;
    min-width: 160px;
    width: 100%;
    height: 40px;
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    display: none;
  }
`;

const OrderQuickAddComponent = props => {
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    onAddToCart,
    selectedProduct,
    addToCartLoading,
    addToCartEnabled,
    onConfirmSelect,
    onRemoveItem,
    inputProducts,
    errors,
    onCancel,
    onSelect,
    onInputChange,
    onChangeQuantity,
    input
  } = props;
  const [err] = errors;

  let buttonFn = noop;
  if (err?.type === 'notFound') {
    buttonFn = () => onConfirmSelect(err.productCode);
  } else if (err?.type === 'notAvailable') {
    buttonFn = () => onRemoveItem(err.productCode);
  } else if (err?.type === 'quantity') {
    buttonFn = () => onChangeQuantity(err.productCode, err.foundProduct);
  }
  return (
    <StyledQuickAddContainer theme={ theme }>
      <p className="heading" dangerouslySetInnerHTML={ { __html: t('orders.quickAddLabel') } } />
      <div className="cds--row">
        <div className="left">
          { errors.filter(error => error.type !== 'err').length > 0
            ? (
              <div className="textarea disabled">
                { inputProducts.map(([code, qty]) => (
                  <p key={ code }>
                    <span className={ `code ${ err?.productCode === code ? 'error' : '' }` }>
                      { code }
                    </span>
                    <span className="quantity">{ qty }</span>
                  </p>
                )) }
              </div>
            )
            : (
              <TextArea
                className="textarea"
                value={ input }
                labelText=""
                onChange={ onInputChange }
                helperText=""
                cols={ 50 }
                rows={ 5 }
                disabled={ addToCartLoading }
                id="quickAddTextArea"
              />
            )
          }
          { err && (
            <div className="suggestions">
              <p
                className={ err.type === 'err' ? 'error' : '' }
                dangerouslySetInnerHTML={ { __html: err.message } }
              />
              { err.suggestedProducts?.length > 0 && (
                <div className="products">
                  <RadioButtonGroup
                    name="searchQuerySimilarProducts"
                    defaultSelected={ null }
                    valueSelected={ selectedProduct?.code }
                    orientation="vertical"
                  >
                    { err.suggestedProducts.map(product => (
                      <RadioButton
                        className="radioOption"
                        id={ product.code }
                        value={ product.code }
                        name={ product.code }
                        key={ product.code }
                        onClick={ () => onSelect(product, err.productCode) }
                        labelText={(
                          <p>
                            <span className="code">{ product.code }</span>
                            <span>{ product.title } ({ product.price })</span>
                          </p>
                        )}
                      />
                    )) }
                  </RadioButtonGroup>
                </div>
              ) }
              { err.type !== 'err' && (
                <div className="actions">
                  <Button
                    kind="danger"
                    className="red"
                    disabled={ err?.type === 'notFound' && !selectedProduct }
                    onClick={ buttonFn }
                  >
                    { err?.type === 'notFound' ? t('common.choose') : '' }
                    { err?.type === 'notAvailable' ? t('common.remove') : '' }
                    { err?.type === 'quantity' ? t('common.fixQty') : '' }
                  </Button>
                  { err?.type === 'notFound' && (
                    <Button
                      kind="ghost"
                      className="red"
                      onClick={ () => onRemoveItem(err.productCode) }
                    >
                      { t('common.remove') }
                    </Button>
                  ) }
                  <Button
                    kind="ghost"
                    className="red"
                    onClick={ onCancel }
                  >
                    { t('common.cancel') }
                  </Button>
                </div>
              ) }
            </div>
          ) }
        </div>
        <div className="right">
          <Button
            kind="ghost"
            className="blue addToCart"
            onClick={ onAddToCart }
            disabled={ !inputProducts.length || !addToCartEnabled || addToCartLoading }
          >
            { addToCartLoading && <Loading active small withOverlay={ false } /> }
            { t('products.addToCart') }
          </Button>
        </div>
      </div>
    </StyledQuickAddContainer>
  );
};

OrderQuickAddComponent.propTypes = {
  inputProducts: array,
  input: string,
  onInputChange: func,
  onAddToCart: func,
  onCancel: func,
  onConfirmSelect: func,
  onSelect: func,
  errors: array,
  onRemoveItem: func,
  onChangeQuantity: func,
  selectedProduct: object,
  addToCartLoading: bool,
  addToCartEnabled: bool
};

OrderQuickAddComponent.displayName = 'OrderQuickAddComponent';

export default OrderQuickAddComponent;
